import { useEffect, useRef, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { locationApi } from '@/lib/api-paths';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { TERMS } from '@pageboss/shared';
import { buttonVariants } from '@/components/ui/button';
import { api, type ApiError } from '@/lib/api';
import type { GbpNapDivergence, GbpNapField } from '@/lib/types';
import { cn } from '@/lib/utils';

const FIELD_LABEL: Record<GbpNapField, string> = {
  name: 'Business name',
  phone: 'Phone number',
  address: 'Address',
};

// Mirrors napFieldRisk (apps/dashboard/src/lib/nap-apply.ts) client-side:
// name/address can trigger a Google re-verification that temporarily hides
// the listing (§4.3); phone is a one-click, no-warning write.
const HIGH_RISK: Record<GbpNapField, boolean> = {
  name: true,
  address: true,
  phone: false,
};

// Server answers `{ error: '<code>' }` on failure — ApiError's `.body` is
// the parsed JSON, so read the code off it rather than the derived message
// (same convention as review-reply-box.tsx's apiErrorCode).
function apiErrorCode(err: unknown): string | undefined {
  const body = (err as ApiError | undefined)?.body;
  return body && typeof body === 'object' && 'error' in body
    ? String((body as { error: unknown }).error)
    : undefined;
}

// Never render a raw server code. risk_confirmation_required and
// no_divergence/nothing_to_write are races the UI shouldn't normally hit
// (confirmRisk is only sent from the warning state; the row only exists
// while a real divergence is showing) — the fallback sentence covers them
// honestly without inventing a specific explanation.
const APPLY_ERRORS: Record<string, string> = {
  reauth_needed: 'Your Google connection needs to be renewed. Reconnect from the top of this page.',
  apply_rejected: "Google didn't accept this change. You can still make it on Google directly.",
  not_verified: 'This listing needs to be verified with Google before we can write to it.',
  gbp_unreachable: "Couldn't reach Google just now. Try again in a moment.",
};
const APPLY_ERROR_FALLBACK = "That didn't go through. Try again, or make the change on Google directly.";
function applyErrorMessage(code: string | undefined): string {
  return (code && APPLY_ERRORS[code]) ?? APPLY_ERROR_FALLBACK;
}

// FIX 8 (S3 review, pre-existing on this card since S1+S2): resolve()'s
// catch used to render err.message raw ('locked', 'no_location_data', …) —
// a bare server code, not a sentence. Same apiErrorCode + map convention as
// APPLY_ERRORS above, sized to what /nap-resolve can actually return.
const RESOLVE_ERRORS: Record<string, string> = {
  locked: "Your current plan doesn't include this anymore. Check your plan in Billing.",
  gbp_unavailable: "Google features aren't available right now. Try again later.",
  not_connected: "This site's Google connection was removed. Reconnect from the top of this page.",
  no_location_data: "Couldn't read this listing from Google yet. Try again in a moment.",
};
const RESOLVE_ERROR_FALLBACK = 'Could not save that right now. Try again in a moment.';
function resolveErrorMessage(code: string | undefined): string {
  return (code && RESOLVE_ERRORS[code]) ?? RESOLVE_ERROR_FALLBACK;
}

// design brief §5.5 — OPERATOR DECISION: sits at the very top of the page,
// above the location card, and only renders when a real divergence exists
// (the caller already gates on `gbp.napDivergence` being present). diffNap
// (lib/nap-compare.ts) only emits an entry when BOTH sides have a value and
// they genuinely differ, so `site`/`google` are never null in practice —
// the null-filtering below is defense-in-depth, not the expected path, and
// silently drops (never fabricates) anything that violates that invariant.
export function GbpNapDivergenceCard({
  siteId,
  napDivergence,
  placeId,
  onResolved,
}: {
  siteId: string;
  napDivergence: GbpNapDivergence;
  placeId: string | null;
  // Passing an announcement is what makes this success audible: the screen
  // reads it out in its live region and moves focus. See routes/site-gbp.tsx.
  onResolved: (announcement?: string) => void;
}) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const [saving, setSaving] = useState<'google' | 'site' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolvedAsSite, setResolvedAsSite] = useState(false);
  const copyPanelRef = useRef<HTMLElement | null>(null);

  // The "my site is correct" answer swaps this card's content in place
  // rather than refetching, so the screen-level announcement never fires and
  // the button that was focused is unmounted — focus would land on <body>.
  // Move it onto the replacement panel, which is also role="status" so the
  // change is spoken rather than silent.
  useEffect(() => {
    if (resolvedAsSite) copyPanelRef.current?.focus();
  }, [resolvedAsSite]);

  const fields = napDivergence.fields.filter((f) => f.site !== null && f.google !== null);
  if (fields.length === 0) return null;

  async function resolve(choice: 'google' | 'site') {
    setSaving(choice);
    setError(null);
    try {
      await api.post(locationApi(businessId, siteId, 'gbp/nap-resolve'), { choice });
      if (choice === 'google') {
        onResolved('Saved. Your business details now match what Google has.');
      } else {
        setResolvedAsSite(true);
      }
    } catch (err) {
      setError(resolveErrorMessage(apiErrorCode(err)));
    } finally {
      setSaving(null);
    }
  }

  if (resolvedAsSite) {
    return (
      <section
        ref={copyPanelRef}
        tabIndex={-1}
        role="status"
        // Programmatic focus on a non-interactive container: no interactive
        // control loses its ring here (design brief §7 / global a11y rule).
        className="rounded-xl border border-border-subtle bg-card p-6 focus:outline-none"
      >
        <h2 className="text-base font-semibold tracking-tight">Update these on Google</h2>
        <p className="mt-1 text-sm text-text-secondary">
          Saved. Copy each value below into your {TERMS.googleProfile}.
        </p>
        <ul className="mt-4 flex flex-col gap-2">
          {fields.map((f) => (
            <NapRow
              key={f.field}
              siteId={siteId}
              field={f.field}
              label={FIELD_LABEL[f.field]}
              value={f.site as string}
              onApplied={onResolved}
            />
          ))}
        </ul>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {placeId ? (
            <a
              href={`https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(placeId)}`}
              target="_blank"
              rel="noreferrer"
              className={buttonVariants({ size: 'sm', variant: 'outline' })}
            >
              Open {TERMS.googleProfile}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          ) : null}
          <button
            type="button"
            // Wrapped, not passed by reference: onResolved's first argument
            // is the screen's spoken announcement, and handing it a
            // MouseEvent would read out an object.
            //
            // It DOES pass one. Dismissing unmounts this whole card for good
            // (the answer is already recorded, so unresolvedDiffs filters
            // every field and the server stops sending napDivergence), which
            // means a keyboard user lands on <body> and a screen-reader user
            // hears nothing unless the screen speaks. The sentence is true:
            // §6.3's suppression re-raises only if Google's side changes.
            onClick={() =>
              onResolved("Closed. We won't ask about this again unless Google's details change.")
            }
            className={buttonVariants({ size: 'sm', variant: 'ghost' })}
          >
            Done
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-border-subtle bg-card p-6">
      <h2 className="text-base font-semibold tracking-tight">Your info doesn't match Google</h2>
      <p className="mt-1 text-sm text-text-secondary">
        Customers see different details depending on where they find you. Pick which one is right.
      </p>

      <ul className="mt-4 flex flex-col gap-3">
        {fields.map((f) => (
          <li key={f.field} className="rounded-lg border border-border-subtle bg-surface-page p-3">
            <p className="eyebrow">
              {FIELD_LABEL[f.field]}
            </p>
            <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
              <div>
                <p className="text-xs text-text-tertiary">Google</p>
                <p className="text-sm text-foreground">{f.google}</p>
              </div>
              <div>
                <p className="text-xs text-text-tertiary">Your site</p>
                <p className="text-sm text-foreground">{f.site}</p>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {error ? (
        <p className="mt-3 text-sm text-danger-fg" role="alert">
          {error}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          disabled={saving !== null}
          onClick={() => void resolve('google')}
          className={buttonVariants({ size: 'sm' })}
        >
          {saving === 'google' ? 'Saving…' : "Google's is correct"}
        </button>
        <button
          type="button"
          disabled={saving !== null}
          onClick={() => void resolve('site')}
          className={buttonVariants({ size: 'sm', variant: 'outline' })}
        >
          {saving === 'site' ? 'Saving…' : 'My site is correct'}
        </button>
      </div>
    </section>
  );
}

// S1+S2's CopyRow, extended per S3 §4.3 / screen-design addendum §C: phone
// (low risk) gets a one-click "Update it on Google for me" next to Copy;
// name/address (high risk) show the re-verification warning ALWAYS, not
// behind a click — §C is explicit that the warning is not tıkla-aç, so
// there is no async-reveal focus problem. confirmRisk is only ever sent
// from that warning's "Update it anyway" button. "I'll do it on Google
// myself" makes no server call and permanently falls back to plain CopyRow
// for the life of this mount — S1+S2's copy-by-hand path, preserved.
function NapRow({
  siteId,
  field,
  label,
  value,
  onApplied,
}: {
  siteId: string;
  field: GbpNapField;
  label: string;
  value: string;
  onApplied: (announcement?: string) => void;
}) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const highRisk = HIGH_RISK[field];
  const [declined, setDeclined] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function apply(confirmRisk: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.post(locationApi(businessId, siteId, 'gbp/nap-apply'), {
        field,
        ...(confirmRisk ? { confirmRisk: true } : {}),
      });
      setSuccess(true);
      // The fresh Google read on the server already purged the location
      // cache, so the parent's refetch is what makes this row's field drop
      // out of napDivergence.fields on truth — not a client-side guess.
      onApplied(`${label} updated on Google.`);
    } catch (err) {
      setError(applyErrorMessage(apiErrorCode(err)));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can fail (permissions, insecure context). The
      // value is still visible to select and copy by hand — soft failure.
    }
  }

  // Declining swaps this row's whole subtree for CopyRow's — the button
  // that was just clicked ("I'll do it on Google myself") unmounts along
  // with it, and without focusOnMount focus would silently fall to <body>
  // with nothing announced. focusOnMount moves it onto CopyRow's own Copy
  // button, the natural next control in this row (same pattern as this
  // file's copyPanelRef for the sibling "my site is correct" swap).
  if (declined) return <CopyRow label={label} value={value} focusOnMount />;

  return (
    <li className="rounded-lg border border-border-subtle bg-surface-page px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-text-tertiary">{label}</p>
          <p className="truncate text-sm text-foreground">{value}</p>
        </div>
        {!highRisk && !success ? (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void apply(false)}
              className={buttonVariants({ size: 'sm' })}
            >
              {busy ? 'Updating…' : 'Update it on Google for me'}
            </button>
            <button
              type="button"
              onClick={() => void copy()}
              className={buttonVariants({ size: 'sm', variant: 'outline' })}
            >
              {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        ) : null}
      </div>

      {success ? (
        <p className="mt-2 text-xs text-success-fg">Updated — Google may take a few minutes to show it.</p>
      ) : highRisk ? (
        <div className="mt-2 rounded-md bg-warning-bg px-3 py-2 text-sm text-warning-fg">
          <p>
            We can make this change on Google for you. Heads up: Google often asks to re-verify your
            listing when the name or address changes, and your listing may briefly disappear from
            search while that happens.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void apply(true)}
              className={buttonVariants({ size: 'sm' })}
            >
              {busy ? 'Updating…' : 'Update it anyway'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setDeclined(true)}
              className={buttonVariants({ size: 'sm', variant: 'outline' })}
            >
              I'll do it on Google myself
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="mt-2 text-xs text-danger-fg" role="alert">
          {error}
        </p>
      ) : null}
    </li>
  );
}

function CopyRow({
  label,
  value,
  focusOnMount = false,
}: {
  label: string;
  value: string;
  // Only true from NapRow's decline swap (see there for why). The plain
  // S1+S2 callers of this row never pass it, so nothing changes for them.
  focusOnMount?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const copyButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (focusOnMount) copyButtonRef.current?.focus();
    // Runs once on mount only — this row is freshly mounted exactly when
    // the focus needs to move, never re-rendered in place with a changed
    // prop, so there's nothing to re-run on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can fail (permissions, insecure context). The
      // value is still visible to select and copy by hand — soft failure.
    }
  }

  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border border-border-subtle bg-surface-page px-3 py-2">
      <div className="min-w-0">
        <p className="text-xs text-text-tertiary">{label}</p>
        <p className="truncate text-sm text-foreground">{value}</p>
      </div>
      <button
        ref={copyButtonRef}
        type="button"
        onClick={() => void copy()}
        className={cn(buttonVariants({ size: 'sm', variant: 'outline' }), 'shrink-0')}
      >
        {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </li>
  );
}
